/**
 * Sysadmin Models — Tenant assignment matrix (Wave 3 / M6c Phase E).
 *
 * Criterion 4: per-tenant brand → engine binding. Sysadmin chooses
 * which engine in a brand's pool serves each tenant + toggles
 * availability + records sysadmin-only notes.
 *
 * The b58 backfill (M6b-followup) seeds one assignment row per
 * (org × brand) so every existing tenant lands here with a default
 * engine. Phase E delivers the editing UI on top of that data.
 *
 * Per I-M6.7 the matrix is sysadmin-only; tenant chat UI never sees
 * engine names, provenance, or sysadmin notes — only enabled brand
 * IDs.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Grid3x3, Check, AlertTriangle, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useAssignments,
  useArgusModels,
  useArgusModelDetail,
  useUpdateAssignment,
  type AssignmentRow,
} from '@/lib/use-argus-models';
import { ModelsSubNav } from './models/index';

export const Route = createFileRoute('/sysadmin/models/assignments')({
  component: AssignmentsPage,
});

function AssignmentsPage() {
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [editing, setEditing] = useState<AssignmentRow | null>(null);

  const assignmentsQuery = useAssignments({});
  const brandsQuery = useArgusModels();

  const filtered = useMemo(() => {
    const items = assignmentsQuery.data?.items ?? [];
    return items.filter((a) => {
      if (
        search &&
        !a.organizationName.toLowerCase().includes(search.toLowerCase()) &&
        !a.brandId.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (brandFilter !== 'all' && a.brandId !== brandFilter) return false;
      if (enabledFilter === 'enabled' && !a.isEnabled) return false;
      if (enabledFilter === 'disabled' && a.isEnabled) return false;
      return true;
    });
  }, [assignmentsQuery.data, search, brandFilter, enabledFilter]);

  const stats = useMemo(() => {
    const items = assignmentsQuery.data?.items ?? [];
    const total = items.length;
    const enabled = items.filter((a) => a.isEnabled).length;
    const tenants = new Set(items.map((a) => a.organizationId)).size;
    const brands = new Set(items.map((a) => a.brandId)).size;
    return { total, enabled, tenants, brands };
  }, [assignmentsQuery.data]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Grid3x3 className="h-6 w-6" />
          Tenant Assignments
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Per-tenant brand → engine binding. Sysadmin chooses which engine
          serves each tenant + toggles availability. Tenant chat picker only
          shows enabled assignments.
        </p>
      </div>

      <ModelsSubNav active="/sysadmin/models/assignments" />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard label="Assignments" value={stats.total} />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          hint={`${stats.total - stats.enabled} disabled`}
        />
        <StatCard label="Tenants" value={stats.tenants} />
        <StatCard label="Brands in use" value={stats.brands} />
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by tenant name or brand…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  {brandsQuery.data?.items.map((b) => (
                    <SelectItem key={b.id} value={b.brandId}>
                      {b.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={enabledFilter}
                onValueChange={(v) =>
                  setEnabledFilter(v as 'all' | 'enabled' | 'disabled')
                }
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  <SelectItem value="enabled">Enabled only</SelectItem>
                  <SelectItem value="disabled">Disabled only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Assignment matrix
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({filtered.length} of {assignmentsQuery.data?.items.length ?? 0})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {assignmentsQuery.isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center space-y-2">
              <Grid3x3 className="h-10 w-10 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">
                {assignmentsQuery.data?.items.length === 0
                  ? 'No assignments yet. Onboard a tenant to seed the matrix.'
                  : 'No assignments match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Tenant</TableHead>
                    <TableHead className="w-[160px]">Brand</TableHead>
                    <TableHead className="w-[260px]">Engine</TableHead>
                    <TableHead className="w-[120px]">Provenance</TableHead>
                    <TableHead className="w-[100px]">Enabled</TableHead>
                    <TableHead className="w-[180px]">Last modified</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <AssignmentRowView
                      key={a.id}
                      row={a}
                      onEdit={() => setEditing(a)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditAssignmentDialog row={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wider">
          {label}
        </div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function provenanceClass(p: string): string {
  if (p === 'us-only') {
    return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30';
  }
  if (p === 'us-eu') {
    return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  }
  return 'bg-muted text-muted-foreground border-border';
}

function AssignmentRowView({
  row,
  onEdit,
}: {
  row: AssignmentRow;
  onEdit: () => void;
}) {
  const updateMutation = useUpdateAssignment();

  const handleToggle = async (next: boolean) => {
    await updateMutation.mutateAsync({ id: row.id, isEnabled: next });
  };

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-sm">{row.organizationName}</div>
        <div className="text-xs text-muted-foreground font-mono">
          {row.organizationId.slice(0, 8)}…
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="font-mono text-xs">
          {row.brandId}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="text-sm">{row.engineRoleLabel}</div>
        <div className="text-xs text-muted-foreground font-mono">
          {row.engineBaseLlmKey}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={provenanceClass(row.engineProvenance)}>
          {row.engineProvenance}
        </Badge>
      </TableCell>
      <TableCell>
        <Switch
          checked={row.isEnabled}
          onCheckedChange={handleToggle}
          disabled={updateMutation.isPending}
          aria-label={`Toggle ${row.brandId} for ${row.organizationName}`}
        />
      </TableCell>
      <TableCell>
        <span className="text-xs text-muted-foreground">
          {new Date(row.lastModifiedAt).toLocaleString()}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onEdit} title="Edit engine + notes">
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
}

function EditAssignmentDialog({
  row,
  onClose,
}: {
  row: AssignmentRow;
  onClose: () => void;
}) {
  const detailQuery = useArgusModelDetail(row.argusModelId);
  const updateMutation = useUpdateAssignment();

  const [engineId, setEngineId] = useState(row.argusModelEngineId);
  const [notes, setNotes] = useState(row.sysadminNotes ?? '');
  const [isEnabled, setIsEnabled] = useState(row.isEnabled);

  const engines = detailQuery.data?.engines ?? [];

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      id: row.id,
      argusModelEngineId:
        engineId !== row.argusModelEngineId ? engineId : undefined,
      sysadminNotes: notes !== (row.sysadminNotes ?? '') ? notes : undefined,
      isEnabled: isEnabled !== row.isEnabled ? isEnabled : undefined,
    });
    onClose();
  };

  const dirty =
    engineId !== row.argusModelEngineId ||
    notes !== (row.sysadminNotes ?? '') ||
    isEnabled !== row.isEnabled;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit assignment</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{row.organizationName}</span> →{' '}
            <Badge variant="outline" className="font-mono text-xs ml-1">
              {row.brandId}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Engine</label>
            <Select value={engineId} onValueChange={setEngineId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {engines.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    <div className="flex items-center gap-2">
                      <span>{e.roleLabel}</span>
                      <span className="text-xs text-muted-foreground font-mono">
                        ({e.baseLlmModelKey})
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 px-1">
                        {e.provenanceEligibility.join(', ')}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Switching the engine takes effect on the tenant's next chat
              request. In-flight responses are not interrupted.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Enabled</div>
              <div className="text-xs text-muted-foreground">
                When off, this brand is hidden from the tenant's chat picker.
              </div>
            </div>
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Sysadmin notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes — not visible to tenants."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Per I-M6.7 these notes never reach the tenant. Use for ops
              context (e.g. "switched off vision while we audit DPIA").
            </p>
          </div>

          {row.engineProvenance === 'us-only' && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-700 dark:text-amber-300">
                This engine is bound to{' '}
                <span className="font-mono">us-only</span> provenance. Verify
                the tenant has US-Person attestation before enabling for ITAR
                data.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!dirty || updateMutation.isPending}>
            {updateMutation.isPending ? (
              'Saving…'
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Save changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
